import { useState } from "react";
import { SALES_DEFINITIONS } from "./salesDefinitions.ts";
import { Link } from "react-router";
import { Table } from "lucide-react";
import { SaleOutcome, SaleRecord, SalesGateway } from "./SaleRecord.ts";
import { GatewayOutcomes } from "./salesBreakdowns.ts";
import {
  declineReasonLabelOf,
  OUTCOME_STYLES,
  OUTCOMES_IN_ORDER,
  SALE_OUTCOME_LABELS,
  SALES_GATEWAY_LABELS,
} from "./salesLabels.ts";
import { formatCount, formatPercent } from "./salesFormat.ts";
import { SalesPeriod } from "./salesPeriod.ts";
import { EmptyStatistics, LoadingBlock } from "./StatisticsCard.tsx";
import { StatisticsCardWithDetails } from "./StatisticsCardWithDetails.tsx";
import { numberOfOrdersOf } from "./salesCustomers.ts";
import {
  CUSTOMER_IDENTITY_COLUMNS,
  CUSTOMER_LAST_ORDER_DATE_COLUMN,
  CUSTOMER_ORDER_COUNT_COLUMN,
  CUSTOMER_SOLD_COLUMN,
  customerOutcomeCountColumns,
  ORDER_AMOUNT_COLUMN,
  ORDER_CUSTOMER_COLUMNS,
  ORDER_DATE_COLUMN,
  ORDER_DECLINE_REASON_COLUMN,
  ORDER_GATEWAY_COLUMN,
  ORDER_ID_COLUMN,
  ORDER_OUTCOME_COLUMN,
  ORDER_PAYMENT_METHOD_COLUMN,
  orderItemColumn,
} from "./salesDetailsColumns.tsx";
import { downloadSalesCsv, ratioForCsv, toCsv } from "./salesCsv.ts";
import { ordersListPathFor } from "./ordersListLinks.ts";

const DECLINE_REASONS_SHOWN = 4;

type HoveredSegment = { gateway: SalesGateway; outcome: SaleOutcome };

function OutcomeLegend({ outcomes }: { outcomes: SaleOutcome[] }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {outcomes.map((outcome) => {
        const { color, Icon } = OUTCOME_STYLES[outcome];
        return (
          <span
            key={outcome}
            className="flex items-center gap-1 text-xs text-slate-500"
          >
            <Icon className="w-3.5 h-3.5" style={{ color }} aria-hidden />
            {SALE_OUTCOME_LABELS[outcome]}
          </span>
        );
      })}
    </div>
  );
}

function OutcomesBar({
  gateway,
  hoveredOutcome,
  onHover,
}: {
  gateway: GatewayOutcomes;
  hoveredOutcome: SaleOutcome | null;
  onHover: (outcome: SaleOutcome | null) => void;
}) {
  return (
    <div className="flex h-3 w-full gap-0.5" onMouseLeave={() => onHover(null)}>
      {OUTCOMES_IN_ORDER.filter(
        (outcome) => gateway.countByOutcome[outcome] > 0,
      ).map((outcome) => (
        <button
          key={outcome}
          type="button"
          aria-label={`${SALE_OUTCOME_LABELS[outcome]}: ${gateway.countByOutcome[outcome]}`}
          onMouseEnter={() => onHover(outcome)}
          onFocus={() => onHover(outcome)}
          onBlur={() => onHover(null)}
          className="h-full rounded-sm first:rounded-l-full last:rounded-r-full transition-opacity"
          style={{
            flexGrow: gateway.countByOutcome[outcome],
            backgroundColor: OUTCOME_STYLES[outcome].color,
            opacity: hoveredOutcome && hoveredOutcome !== outcome ? 0.35 : 1,
          }}
        />
      ))}
    </div>
  );
}

function OutcomesTable({
  outcomes,
  columns,
}: {
  outcomes: GatewayOutcomes[];
  columns: SaleOutcome[];
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-gray-500 border-b border-gray-200">
            <th className="py-2 pr-3 font-medium">Gateway</th>
            <th className="py-2 pr-3 font-medium text-right">Ordini</th>
            {columns.map((outcome) => (
              <th key={outcome} className="py-2 pr-3 font-medium text-right">
                {SALE_OUTCOME_LABELS[outcome]}
              </th>
            ))}
            <th className="py-2 font-medium text-right">% conclusi</th>
          </tr>
        </thead>
        <tbody>
          {outcomes.map((gateway) => (
            <tr
              key={gateway.gateway}
              className="border-b border-gray-100 text-gray-700"
            >
              <td className="py-1.5 pr-3">
                {SALES_GATEWAY_LABELS[gateway.gateway]}
              </td>
              <td className="py-1.5 pr-3 text-right">
                {formatCount(gateway.orderCount)}
              </td>
              {columns.map((outcome) => (
                <td key={outcome} className="py-1.5 pr-3 text-right">
                  {formatCount(gateway.countByOutcome[outcome])}
                </td>
              ))}
              <td className="py-1.5 text-right font-medium">
                {formatPercent(gateway.completionRate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function GatewayOutcomesCard({
  outcomes,
  orders,
  itemLabelOf,
  period,
  isLoading,
}: {
  outcomes: GatewayOutcomes[];
  orders: SaleRecord[];
  itemLabelOf: (itemKey: string) => string;
  period: SalesPeriod;
  isLoading: boolean;
}) {
  const outcomesOfTheOrders = OUTCOMES_IN_ORDER.filter((outcome) =>
    orders.some((order) => order.outcome === outcome),
  );

  const exportCsv = () =>
    downloadSalesCsv({
      section: "esiti",
      period,
      csv: toCsv(outcomes, [
        {
          header: "Gateway",
          valueOf: (gateway) => SALES_GATEWAY_LABELS[gateway.gateway],
        },
        { header: "Ordini", valueOf: (gateway) => gateway.orderCount },
        ...OUTCOMES_IN_ORDER.map((outcome) => ({
          header: SALE_OUTCOME_LABELS[outcome],
          valueOf: (gateway: GatewayOutcomes) =>
            gateway.countByOutcome[outcome],
        })),
        {
          header: "% conclusi",
          valueOf: (gateway) => ratioForCsv(gateway.completionRate),
        },
      ]),
    });

  return (
    <StatisticsCardWithDetails
      title="Esiti degli ordini"
      info={SALES_DEFINITIONS.outcomes}
      description="Ordini creati nel periodo, per gateway"
      csvSection="esiti"
      period={period}
      isLoading={isLoading}
      exportSummaryCsv={outcomes.length > 0 ? exportCsv : undefined}
      orders={orders}
      orderColumns={[
        ORDER_ID_COLUMN,
        ORDER_DATE_COLUMN,
        ...ORDER_CUSTOMER_COLUMNS,
        orderItemColumn(itemLabelOf),
        ORDER_GATEWAY_COLUMN,
        ORDER_PAYMENT_METHOD_COLUMN,
        ORDER_OUTCOME_COLUMN,
        ORDER_DECLINE_REASON_COLUMN,
        ORDER_AMOUNT_COLUMN,
      ]}
      customerColumns={[
        ...CUSTOMER_IDENTITY_COLUMNS,
        CUSTOMER_ORDER_COUNT_COLUMN,
        ...customerOutcomeCountColumns(outcomesOfTheOrders),
        CUSTOMER_SOLD_COLUMN,
        CUSTOMER_LAST_ORDER_DATE_COLUMN,
      ]}
      rankCustomersBy={numberOfOrdersOf}
      noOrdersMessage="Nessun ordine creato nel periodo"
      summary={
        <OutcomesSummary
          outcomes={outcomes}
          period={period}
          isLoading={isLoading}
        />
      }
    />
  );
}

function OutcomesSummary({
  outcomes,
  period,
  isLoading,
}: {
  outcomes: GatewayOutcomes[];
  period: SalesPeriod;
  isLoading: boolean;
}) {
  const [hovered, setHovered] = useState<HoveredSegment | null>(null);
  const [isShowingTable, setIsShowingTable] = useState(false);
  const presentOutcomes = OUTCOMES_IN_ORDER.filter((outcome) =>
    outcomes.some((gateway) => gateway.countByOutcome[outcome] > 0),
  );
  const gatewaysWithDeclines = outcomes.filter(
    (gateway) => gateway.declineReasons.length > 0,
  );

  return (
    <>
      {isLoading ? (
        <LoadingBlock className="h-48 w-full" />
      ) : outcomes.length === 0 ? (
        <EmptyStatistics>Nessun ordine creato nel periodo</EmptyStatistics>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4">
            {outcomes.map((gateway) => {
              const hoveredOutcome =
                hovered?.gateway === gateway.gateway ? hovered.outcome : null;
              return (
                <div key={gateway.gateway} className="flex flex-col gap-1.5">
                  <div className="flex items-baseline justify-between gap-3 text-sm">
                    <Link
                      to={ordersListPathFor({
                        gateway: gateway.gateway,
                        period,
                      })}
                      className="font-medium text-gray-800 hover:text-blue-600 hover:underline"
                    >
                      {SALES_GATEWAY_LABELS[gateway.gateway]}
                    </Link>
                    <span className="text-xs text-gray-500">
                      {formatCount(gateway.orderCount)} ordini ·{" "}
                      <span className="font-medium text-gray-700">
                        {formatPercent(gateway.completionRate)} conclusi
                      </span>
                    </span>
                  </div>
                  <OutcomesBar
                    gateway={gateway}
                    hoveredOutcome={hoveredOutcome}
                    onHover={(outcome) =>
                      setHovered(
                        outcome ? { gateway: gateway.gateway, outcome } : null,
                      )
                    }
                  />
                  <span className="h-4 text-xs text-gray-500">
                    {hoveredOutcome &&
                      `${SALE_OUTCOME_LABELS[hoveredOutcome]}: ${formatCount(gateway.countByOutcome[hoveredOutcome])} (${formatPercent(gateway.countByOutcome[hoveredOutcome] / gateway.orderCount)})`}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <OutcomeLegend outcomes={presentOutcomes} />
            <button
              type="button"
              onClick={() => setIsShowingTable((isShowing) => !isShowing)}
              className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
            >
              <Table className="w-3.5 h-3.5" aria-hidden />
              {isShowingTable ? "Nascondi tabella" : "Mostra tabella"}
            </button>
          </div>
          {isShowingTable && (
            <OutcomesTable outcomes={outcomes} columns={presentOutcomes} />
          )}
          {gatewaysWithDeclines.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-gray-100 pt-3">
              {gatewaysWithDeclines.map((gateway) => (
                <div key={gateway.gateway} className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-gray-600">
                    Motivi di rifiuto {SALES_GATEWAY_LABELS[gateway.gateway]}
                  </span>
                  <ul className="flex flex-col gap-1">
                    {gateway.declineReasons
                      .slice(0, DECLINE_REASONS_SHOWN)
                      .map(({ declineReason, count }) => (
                        <li
                          key={declineReason ?? "none"}
                          className="flex items-start justify-between gap-3 text-xs text-gray-600"
                        >
                          <span>
                            {declineReasonLabelOf(
                              gateway.gateway,
                              declineReason,
                            )}
                          </span>
                          <span className="font-medium text-gray-800">
                            {formatCount(count)}
                          </span>
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
